import { useState, useMemo, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEye,
  faPen,
  faTrash,
  faSearch,
  faUsers,
  faUserCheck,
  faUserSlash,
  faChevronLeft,
  faChevronRight,
  faUserTie,
  faPhone,
  faEnvelope,
  faUser,
  faCircle,
  faEllipsisVertical,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { Card } from "../../../components/ui/card";
import ConfirmDialog from "../../../components/ui/confirmDialog";
import AddOwnerModal from "./AddOwnerModal";
import ChooseOwnerOptionModal from "./ChooseOwnerOptionModal";
import AssignOwnerModal from "./AssignOwnerModal";
import DateFilter from "../../../components/ui/DateFilter";
import { useShopOwners } from "../../../hooks/useShopOwners";
import { useAuth } from "../../../hooks";
import { toastSuccess, toastError } from "../../../utils/toast";

const statusStyles = {
  Active: "bg-green-600 text-white border-green-200",
  Inactive: "bg-red-600 text-white border-red-200",
};

const statusDotStyles = {
  Active: "bg-white",
  Inactive: "bg-red-500",
};

const DropdownMenu = ({
  onView,
  onEdit,
  onRemove,
  onRestore,
  canManage,
  isActive,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => setIsOpen(!isOpen);

  const handleAction = (action) => {
    setIsOpen(false);
    action();
  };

  return (
    <div className="relative">
      <button
        onClick={handleToggle}
        className="p-1.5 rounded-md hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
        aria-label="Actions"
      >
        <FontAwesomeIcon
          icon={faEllipsisVertical}
          className="h-4 w-4 text-gray-600"
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-44 rounded-lg border border-gray-200 bg-white shadow-lg z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
          <div className="py-1">
            <button
              onClick={() => handleAction(onView)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-blue-50 transition-colors text-gray-700"
            >
              <FontAwesomeIcon
                icon={faEye}
                className="text-blue-600 h-3.5 w-3.5"
              />
              View Details
            </button>
            {canManage && (
              <>
                <button
                  onClick={() => handleAction(onEdit)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-blue-50 transition-colors text-gray-700"
                >
                  <FontAwesomeIcon
                    icon={faPen}
                    className="text-blue-600 h-3.5 w-3.5"
                  />
                  Edit Details
                </button>
                <div className="border-t border-gray-100 my-1"></div>
                {isActive ? (
                  <button
                    onClick={() => handleAction(onRemove)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-red-50 transition-colors text-red-600"
                  >
                    <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                    Remove Owner
                  </button>
                ) : (
                  <button
                    onClick={() => handleAction(onRestore)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-green-50 transition-colors text-green-700"
                  >
                    <FontAwesomeIcon
                      icon={faUserCheck}
                      className="h-3.5 w-3.5"
                    />
                    Restore Owner
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
}) => {
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-200">
      <div className="text-sm text-gray-600">
        Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to{" "}
        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
        owners
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="h-9 px-3 border-gray-300 hover:border-blue-800"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3" />
        </Button>
        <div className="flex items-center gap-1">
          {pageNumbers.map((number) => (
            <Button
              key={number}
              variant={currentPage === number ? "default" : "outline"}
              size="sm"
              onClick={() => onPageChange(number)}
              className={`h-9 w-9 p-0 ${
                currentPage === number
                  ? "bg-blue-800 hover:bg-blue-900 text-white"
                  : "border-gray-300 hover:border-blue-800 hover:bg-blue-50"
              }`}
            >
              {number}
            </Button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="h-9 px-3 border-gray-300 hover:border-blue-800"
        >
          <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

const OwnersTab = ({
  shop,
  onAddOwner,
  onViewOwner,
  onEditOwner,
  onRemoveOwner,
}) => {
  const { hasPermission } = useAuth();
  const canManageOwner = hasPermission([
    "manage_business_owner",
    "manage_user_account",
  ]);

  const shopId = shop?.shopId;

  const {
    owners: hookOwners,
    loading,
    refresh,
    setOwnerStatus,
  } = useShopOwners(shopId);

  const owners = useMemo(() => {
    if (!hookOwners) return [];

    return hookOwners.map((item) => ({
      id: item.shop_owner_id || item.id,
      shop_owner_id: item.shop_owner_id,
      shopId: item.shopId,
      username: item.username || item.user?.username || "N/A",
      owner_name:
        item.user?.full_name || item.full_name || item.owner_name || "N/A",
      full_name: item.user?.full_name || item.full_name || "N/A",
      phone: item.user?.phone || item.phone || "N/A",
      email: item.user?.email || item.email || "N/A",
      added_date: item.added_date
        ? new Date(item.added_date).toISOString().split("T")[0]
        : "N/A",
      status: item.status === "active" ? "Active" : "Inactive",
      created_at: item.created_at || item.user?.created_at,
      updated_at: item.updated_at || item.user?.updated_at,
      user: item.user,
      shop: item.shop,
    }));
  }, [hookOwners]);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    ownerId: null,
    ownerName: null,
  });

  const [isChooseModalOpen, setIsChooseModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  const filteredOwners = useMemo(() => {
    let result = owners || [];

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      result = result.filter(
        (o) =>
          o.owner_name?.toLowerCase().includes(search) ||
          o.full_name?.toLowerCase().includes(search) ||
          o.phone?.toLowerCase().includes(search) ||
          o.username?.toLowerCase().includes(search) ||
          o.email?.toLowerCase().includes(search),
      );
    }

    if (isFilterApplied && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((owner) => {
        if (!owner.added_date || owner.added_date === "N/A") return false;
        const addedDate = new Date(owner.added_date);
        return addedDate >= start && addedDate <= end;
      });
    }

    if (statusFilter === "active") {
      result = result.filter((o) => o.status === "Active");
    } else if (statusFilter === "inactive") {
      result = result.filter((o) => o.status === "Inactive");
    }

    return result;
  }, [owners, searchTerm, statusFilter, startDate, endDate, isFilterApplied]);

  const totalItems = filteredOwners.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentOwners = filteredOwners.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, startDate, endDate]);

  const handleRemoveClick = (id, name) => {
    setDeleteDialog({ isOpen: true, ownerId: id, ownerName: name });
  };

  const handleConfirmRemove = async () => {
    if (deleteDialog.ownerId) {
      try {
        await setOwnerStatus(deleteDialog.ownerId, "inactive");
        toastSuccess(`"${deleteDialog.ownerName}" removed successfully!`);
        if (onRemoveOwner) {
          onRemoveOwner(deleteDialog.ownerId);
        }
        await refresh();
      } catch (error) {
        toastError("Failed to remove owner. Please try again.");
        console.error("Error removing owner:", error);
      }
    }
    setDeleteDialog({ isOpen: false, ownerId: null, ownerName: null });
  };

  const handleRestore = async (id, name) => {
    try {
      await setOwnerStatus(id, "active");
      toastSuccess(`"${name}" restored successfully!`);
      if (onRemoveOwner) {
        onRemoveOwner(id);
      }
      await refresh();
    } catch (error) {
      toastError("Failed to restore owner. Please try again.");
      console.error("Error restoring owner:", error);
    }
  };

  const handleCloseDialog = () => {
    setDeleteDialog({ isOpen: false, ownerId: null, ownerName: null });
  };

  const handleAddOwnerSuccess = async (newOwner) => {
    setIsChooseModalOpen(false);
    setIsAddModalOpen(false);
    setIsAssignModalOpen(false);
    if (onAddOwner) {
      onAddOwner(newOwner);
    }
    await refresh();
  };

  const tabs = [
    { id: "all", label: "All Owners", icon: faUsers },
    { id: "active", label: "Active", icon: faUserCheck },
    { id: "inactive", label: "Inactive", icon: faUserSlash },
  ];

  const tabCounts = {
    all: owners?.length || 0,
    active: owners?.filter((o) => o.status === "Active").length || 0,
    inactive: owners?.filter((o) => o.status === "Inactive").length || 0,
  };

  return (
    <div className="space-y-4">
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmRemove}
        title="Remove Owner"
        message={`Are you sure you want to remove "${deleteDialog.ownerName}" from this shop? The owner will be marked as inactive but their record will be kept. You can restore them later.`}
        confirmText="Remove Owner"
        cancelText="Cancel"
        type="danger"
      />

      <ChooseOwnerOptionModal
        isOpen={isChooseModalOpen}
        onClose={() => setIsChooseModalOpen(false)}
        onRegisterNew={() => setIsAddModalOpen(true)}
        onAssignExisting={() => setIsAssignModalOpen(true)}
      />

      <AddOwnerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddOwnerSuccess}
        onUserAdded={handleAddOwnerSuccess}
        shopId={shopId}
      />

      <AssignOwnerModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onAssigned={handleAddOwnerSuccess}
        shopId={shopId}
      />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Shop Owners</h3>
          <p className="text-sm text-gray-600">
            Manage owners associated with this shop
          </p>
        </div>
        {canManageOwner && (
          <Button
            onClick={() => setIsChooseModalOpen(true)}
            className="gap-2 bg-blue-800 hover:bg-blue-900 text-white shadow-sm"
          >
            <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
            Add Owner
          </Button>
        )}
      </div>

      <DateFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApply={() => {
          setIsFilterApplied(true);
          setCurrentPage(1);
        }}
        onClear={() => {
          setStartDate("");
          setEndDate("");
          setIsFilterApplied(false);
          setCurrentPage(1);
        }}
        isFilterApplied={isFilterApplied}
        totalItems={filteredOwners.length}
        label="owners"
      />

      <Card className="bg-white border border-gray-200 px-4 py-3 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-full lg:w-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setStatusFilter(tab.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 flex-1 lg:flex-none justify-center ${
                  statusFilter === tab.id
                    ? "bg-blue-800 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
                }`}
              >
                <FontAwesomeIcon icon={tab.icon} className="h-3 w-3" />
                <span>{tab.label}</span>
                <span
                  className={`ml-0.5 px-1.5 py-0.5 text-xs rounded-full ${
                    statusFilter === tab.id
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {tabCounts[tab.id]}
                </span>
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-72">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 h-3.5 w-3.5"
            />
            <Input
              type="text"
              placeholder="Search by name, phone, email, or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-800/30 focus:border-blue-800 w-full bg-white text-sm h-9"
            />
          </div>
        </div>
      </Card>

      <Card className="bg-white border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-semibold text-gray-700">{totalItems}</span>{" "}
              owners
              {statusFilter !== "all" && (
                <span className="ml-1">({statusFilter})</span>
              )}
              {isFilterApplied && (
                <span className="ml-1 text-blue-600">(filtered by date)</span>
              )}
            </div>
            <div className="text-sm text-gray-600">
              {currentOwners.length > 0 &&
                `Page ${currentPage} of ${totalPages}`}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                  S/N
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                  <FontAwesomeIcon
                    icon={faUserTie}
                    className="mr-1.5 h-3 w-3"
                  />
                  Owner Name
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                  <FontAwesomeIcon icon={faUser} className="mr-1.5 h-3 w-3" />
                  Username
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    className="mr-1.5 h-3 w-3"
                  />
                  Email
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                  <FontAwesomeIcon icon={faPhone} className="mr-1.5 h-3 w-3" />
                  Phone
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                  <FontAwesomeIcon icon={faCircle} className="mr-1.5 h-3 w-3" />
                  Status
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 text-center">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="text-blue-600 h-8 w-8 animate-spin"
                      />
                      <div className="text-gray-600 text-sm">Loading...</div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : currentOwners.length > 0 ? (
                currentOwners.map((owner, idx) => (
                  <TableRow
                    key={owner.id || owner.shop_owner_id}
                    className="hover:bg-blue-50/30 transition-colors duration-150 border-b border-gray-100"
                  >
                    <TableCell className="py-2.5 px-3 text-sm text-gray-600">
                      {startIndex + idx + 1}
                    </TableCell>
                    <TableCell className="py-2.5 px-3 font-medium text-gray-900 text-sm">
                      {owner.owner_name}
                    </TableCell>
                    <TableCell className="py-2.5 px-3 text-sm text-gray-600">
                      {owner.username}
                    </TableCell>
                    <TableCell className="py-2.5 px-3 text-sm text-gray-600">
                      {owner.email}
                    </TableCell>
                    <TableCell className="py-2.5 px-3 text-sm text-gray-600">
                      {owner.phone}
                    </TableCell>
                    <TableCell className="py-2.5 px-3">
                      <Badge
                        variant="outline"
                        className={`${statusStyles[owner.status]} border font-normal px-3 py-1 rounded-full text-xs flex items-center gap-1.5`}
                      >
                        <span
                          className={`inline-block w-1.5 h-1.5 rounded-full ${statusDotStyles[owner.status]}`}
                        />
                        {owner.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2.5 px-3">
                      <div className="flex justify-center">
                        <DropdownMenu
                          canManage={canManageOwner}
                          isActive={owner.status === "Active"}
                          onView={() => onViewOwner(owner)}
                          onEdit={() => onEditOwner(owner)}
                          onRemove={() =>
                            handleRemoveClick(
                              owner.shop_owner_id || owner.id,
                              owner.owner_name,
                            )
                          }
                          onRestore={() =>
                            handleRestore(
                              owner.shop_owner_id || owner.id,
                              owner.owner_name,
                            )
                          }
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <FontAwesomeIcon
                        icon={faUsers}
                        className="text-gray-300 h-8 w-8"
                      />
                      <div className="text-gray-600 text-sm">
                        No owners found
                      </div>
                      <p className="text-gray-600 text-xs">
                        {searchTerm
                          ? "Try adjusting your search terms"
                          : statusFilter !== "all"
                            ? `No ${statusFilter} owners found`
                            : "Add your first owner to get started"}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {totalItems > 0 && !loading && (
          <div className="px-4 py-2.5 border-t border-gray-100">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default OwnersTab;
